<template>
    <v-card class="app-screen-bg pl-2 pl-md-10 message-hub-page">
        <div class="message-hub-page__scroll">
            <div class="message-hub-title-pill-wrap">
                <span class="message-hub-title-pill">{{ $t('messages.hub.title') }}</span>
            </div>

            <!-- stejna data/akce jako playerMessages zalozka v TeamBottomSheet.vue, jen na cele strance misto uzoucke listy -->
            <div class="message-hub-section" v-if="conversations.length">
                <div class="message-hub-section__title">{{ $t('messages.hub.myConversations') }}</div>
                <div
                    class="message-hub-row-list"
                    @touchstart.passive="onListTouchStart($event, 'conv')"
                    @touchend="onListTouchEnd($event, 'conv')"
                >
                    <div
                        v-for="c in pagedConversations"
                        :key="c.otherTrainer.id"
                        class="message-hub-row"
                        :class="[transparentClass, {'message-hub-row--unread': c.unread}]"
                        @click="openChat(c.otherTrainer)"
                    >
                        <div class="message-hub-row__avatar-outer">
                            <div class="message-hub-row__avatar-wrap">
                                <v-img v-if="c.otherTrainer.img" :src="require('@/assets/pic/trainer/' + c.otherTrainer.img)" class="message-hub-row__avatar" position="top center"/>
                                <v-icon v-else size="28">mdi-account</v-icon>
                            </div>
                            <span v-if="c.otherTrainer.isOnline" class="message-hub-row__online-dot"/>
                        </div>
                        <div class="message-hub-row__body">
                            <div class="message-hub-row__name">{{ c.otherTrainer.name }}</div>
                            <div class="message-hub-row__preview">{{ conversationSenderLabel(c) }}: {{ c.lastMessage.text }}</div>
                        </div>
                    </div>
                </div>
                <v-pagination
                    v-if="convPageCount > 1"
                    v-model="convPage"
                    :length="convPageCount"
                    :total-visible="5"
                    class="message-hub-pagination"
                />
            </div>
            <div class="message-hub-empty text-center grey--text pa-4" v-else>
                {{ $t('messages.hub.noConversations') }}
            </div>

            <!-- napsat hraci, se kterym jeste konverzace neexistuje - na rozdil od PvpHub.vue seznamu vyzev se tu bez dotazu nezobrazuje cely seznam hracu -->
            <div class="message-hub-section">
                <div class="message-hub-section__title">{{ $t('messages.hub.writeToPlayer') }}</div>
                <v-text-field
                    v-model="searchQuery"
                    :placeholder="$t('messages.hub.searchPlaceholder')"
                    dense
                    hide-details
                    outlined
                    clearable
                    prepend-inner-icon="mdi-magnify"
                    class="message-hub-search mb-2"
                    @input="onSearchInput"
                />
                <div
                    v-if="searchQuery && searchQuery.trim().length >= 2"
                    class="message-hub-row-list"
                    @touchstart.passive="onListTouchStart($event, 'search')"
                    @touchend="onListTouchEnd($event, 'search')"
                >
                    <div
                        v-for="t in pagedSearchResults"
                        :key="t.id"
                        class="message-hub-row"
                        :class="transparentClass"
                        @click="openChat(t)"
                    >
                        <div class="message-hub-row__avatar-outer">
                            <div class="message-hub-row__avatar-wrap">
                                <v-img v-if="t.img" :src="require('@/assets/pic/trainer/' + t.img)" class="message-hub-row__avatar" position="top center"/>
                                <v-icon v-else size="28">mdi-account</v-icon>
                            </div>
                        </div>
                        <div class="message-hub-row__body">
                            <div class="message-hub-row__name">{{ t.fullName || t.name }}</div>
                            <div class="message-hub-row__preview" v-if="t.experience">{{ $t('messages.hub.playerLevel', {level: t.experience.level}) }}</div>
                        </div>
                    </div>
                    <div v-if="searchResults.length === 0" class="message-hub-empty text-center grey--text pa-4">
                        {{ $t('messages.hub.noPlayerFound') }}
                    </div>
                    <v-pagination
                        v-if="searchPageCount > 1"
                        v-model="searchPage"
                        :length="searchPageCount"
                        :total-visible="5"
                        class="message-hub-pagination"
                    />
                </div>
            </div>
        </div>

        <PlayerChatPopup
            v-model="chatOpenModel"
            :trainer="chatWithTrainer"
            :my-trainer-id="trainerId"
        />
    </v-card>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import PlayerChatPopup from '@/components/journey/next/pieces/PlayerChatPopup';
import mercureClient from '@/utils/mercureClient';

// Vlastni route (/messages), obdoba PvpHub.vue v hamburger menu - znovupouziva presne stejne Vuex akce jako playerMessages zalozka v TeamBottomSheet.vue
export default {
    name: 'MessageHub',
    components: {PlayerChatPopup},
    data() {
        return {
            conversations: [],
            searchQuery: '',
            searchResults: [],
            allPlayers: [],
            chatOpen: false,
            chatWithTrainer: null,
            unsubscribePlayerMessage: null,
            convPage: 1,
            searchPage: 1,
            pageSize: 10,
            convTouchStartX: null,
            searchTouchStartX: null,
        };
    },
    computed: {
        ...mapGetters({
            user: 'user/item',
        }),
        trainerId() {
            return this.user ? this.user.id : null;
        },
        transparentClass() {
            return this.$vuetify.theme.dark ? 'transparent03--dark' : 'transparent03';
        },
        convPageCount() {
            return Math.max(1, Math.ceil(this.conversations.length / this.pageSize));
        },
        pagedConversations() {
            const page = Math.min(this.convPage, this.convPageCount);
            const start = (page - 1) * this.pageSize;
            return this.conversations.slice(start, start + this.pageSize);
        },
        searchPageCount() {
            return Math.max(1, Math.ceil(this.searchResults.length / this.pageSize));
        },
        pagedSearchResults() {
            const page = Math.min(this.searchPage, this.searchPageCount);
            const start = (page - 1) * this.pageSize;
            return this.searchResults.slice(start, start + this.pageSize);
        },
        // zavreni popupu znovu nacte konverzace a obnovi position payload, aby indikator nepectenych zprav v NavDrawer/spodni liste zmizel hned
        chatOpenModel: {
            get() {
                return this.chatOpen;
            },
            set(open) {
                this.chatOpen = open;
                if (!open) {
                    this.closeChat();
                }
            },
        },
    },
    created() {
        this.loadConversations();
        this.loadAllPlayersAction().then((players) => {
            this.allPlayers = players || [];
        });
        this.unsubscribePlayerMessage = mercureClient.onMessage('player_message', this.onPlayerMessageEvent);
    },
    beforeDestroy() {
        if (this.unsubscribePlayerMessage) {
            this.unsubscribePlayerMessage();
        }
    },
    methods: {
        ...mapActions({
            loadPlayerConversationsAction: 'trainer/loadPlayerConversations',
            loadAllPlayersAction: 'trainer/loadAllPlayers',
            loadPosition: 'journey/loadPosition',
        }),
        loadConversations() {
            this.loadPlayerConversationsAction(this.trainerId).then((data) => {
                this.conversations = data || [];
            });
        },
        conversationSenderLabel(c) {
            return c.lastMessage.senderId === this.trainerId ? this.$t('common.you') : c.otherTrainer.name;
        },
        onSearchInput() {
            this.searchPage = 1;
            const query = (this.searchQuery || '').trim().toLowerCase();
            if (query.length < 2) {
                this.searchResults = [];
                return;
            }
            this.searchResults = this.allPlayers.filter((t) =>
                !t.npc && t.id !== this.trainerId && (t.name || '').toLowerCase().indexOf(query) !== -1
            );
        },
        openChat(trainer) {
            this.chatWithTrainer = trainer;
            this.chatOpen = true;
        },
        closeChat() {
            this.chatWithTrainer = null;
            this.loadConversations();
            if (this.user && this.user.position && this.user.position.id) {
                this.loadPosition(this.user.position.id);
            }
        },
        onPlayerMessageEvent(payload) {
            const senderId = payload.sender.id;
            const updatedConversation = {
                otherTrainer: payload.sender,
                lastMessage: payload.message,
                unread: true,
            };
            const idx = this.conversations.findIndex((c) => c.otherTrainer.id === senderId);
            if (idx >= 0) {
                this.conversations.splice(idx, 1, updatedConversation);
            } else {
                this.conversations.unshift(updatedConversation);
            }
            this.conversations.sort((a, b) => new Date(b.lastMessage.created) - new Date(a.lastMessage.created));
        },
        // strankovani pres swipe - stejny vzor (threshold 40px) jako v PvpHub.vue
        onListTouchStart(e, which) {
            const x = e.changedTouches[0].clientX;
            if (which === 'conv') {
                this.convTouchStartX = x;
            } else {
                this.searchTouchStartX = x;
            }
        },
        onListTouchEnd(e, which) {
            const startX = which === 'conv' ? this.convTouchStartX : this.searchTouchStartX;
            if (which === 'conv') {
                this.convTouchStartX = null;
            } else {
                this.searchTouchStartX = null;
            }
            if (startX === null) {
                return;
            }
            const deltaX = e.changedTouches[0].clientX - startX;
            if (Math.abs(deltaX) < 40) {
                return;
            }
            e.preventDefault();
            if (which === 'conv') {
                this.convPage = deltaX < 0
                    ? Math.min(this.convPage + 1, this.convPageCount)
                    : Math.max(this.convPage - 1, 1);
            } else {
                this.searchPage = deltaX < 0
                    ? Math.min(this.searchPage + 1, this.searchPageCount)
                    : Math.max(this.searchPage - 1, 1);
            }
        },
    },
};
</script>

<style scoped>
.message-hub-page {
    height: 100%;
    display: flex;
    flex-direction: column;
}
.message-hub-page__scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding-right: 8px;
    padding-bottom: 24px;
}
.message-hub-title-pill-wrap {
    display: flex;
    justify-content: center;
    padding: 12px 0 8px;
}
.message-hub-title-pill {
    display: inline-block;
    background: rgba(0, 0, 0, .65);
    color: #fff;
    font-size: 20px;
    font-weight: 700;
    padding: 6px 20px;
    border-radius: 20px;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, .6);
}
.message-hub-section {
    padding: 4px 8px 16px;
}
.message-hub-section__title {
    font-size: 16px;
    font-weight: 700;
    padding: 4px 4px 8px;
}
.message-hub-search ::v-deep .v-input__control {
    background: rgba(0, 0, 0, .3);
    border-radius: 8px;
}
.message-hub-row-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.message-hub-pagination {
    margin-top: 8px;
}
.message-hub-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 10px;
    cursor: pointer;
}
.message-hub-row__avatar-outer {
    position: relative;
    flex: none;
}
.message-hub-row__avatar-wrap {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, .08);
}
.message-hub-row__avatar {
    width: 100%;
    height: 100%;
}
.message-hub-row__online-dot {
    position: absolute;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #4CAF50;
    border: 2px solid rgba(0,0,0,0.6);
    right: -1px;
    bottom: -1px;
}
.message-hub-row__body {
    flex: 1 1 auto;
    min-width: 0;
}
.message-hub-row__name {
    font-weight: 700;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.message-hub-row__preview {
    font-size: 12px;
    opacity: .75;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
/* stejny princip jako .team-sheet-conv-row--unread v TeamBottomSheet.vue */
.message-hub-row--unread .message-hub-row__preview {
    opacity: 1;
    font-weight: 700;
}
.message-hub-empty {
    font-size: 13px;
}
</style>
